import type { CalculatorPackage } from '@/api/calculatorPackages';
import type { TradingDeal } from '@/api/admin';
import type { CalculatorSettings, Currency, FXRates } from './EU6Calculator.types';
import { BuyingPoint } from './EU6Calculator.types';

const BUYING_POINTS = new Set<string>(Object.values(BuyingPoint));

export function isLegacyBuyingPointEnum(value: unknown): boolean {
  return typeof value === 'string' && BUYING_POINTS.has(value.trim());
}

export function parsePackageNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function parseDisplayCurrency(currency?: string): Currency | undefined {
  if (!currency) return undefined;
  const code = currency.toUpperCase();
  if (code === 'USD' || code === 'EUR' || code === 'GBP' || code === 'CAD') return code as Currency;
  return undefined;
}

function parseBuyingPointEnum(buyingPoint?: string): BuyingPoint | undefined {
  if (!buyingPoint) return undefined;
  return BUYING_POINTS.has(buyingPoint) ? (buyingPoint as BuyingPoint) : undefined;
}

export function resolveTradingDealFromPackage(
  deals: TradingDeal[],
  pkg: CalculatorPackage
): { tradingDealId: string | null; tradingDealName: string } {
  const storedName = typeof pkg.buyingPoint === 'string' ? pkg.buyingPoint.trim() : '';
  const pct = parsePackageNumber(pkg.tradingDealPercentage);

  if (storedName && !isLegacyBuyingPointEnum(storedName)) {
    const byName = deals.find((d) => d.name === storedName);
    if (byName) return { tradingDealId: byName._id, tradingDealName: byName.name };
    return { tradingDealId: null, tradingDealName: storedName };
  }
  if (pct != null) {
    const byPct = deals.find((d) => d.percentage === pct);
    if (byPct) return { tradingDealId: byPct._id, tradingDealName: byPct.name };
  }

  return { tradingDealId: null, tradingDealName: '' };
}

type DealDisplaySource = Pick<
  CalculatorSettings,
  'tradingDealId' | 'tradingDealName' | 'tradingDealPercentage'
>;

function resolveDealFromSettings(
  settings: DealDisplaySource,
  deals?: TradingDeal[]
): { id: string | null; name: string; percentage: number | undefined } {
  if (settings.tradingDealId && deals?.length) {
    const byId = deals.find((d) => d._id === settings.tradingDealId);
    if (byId) {
      return { id: byId._id, name: byId.name, percentage: byId.percentage };
    }
  }
  const name = settings.tradingDealName?.trim() ?? '';
  if (name && !isLegacyBuyingPointEnum(name)) {
    const byName = deals?.find((d) => d.name === name);
    if (byName) {
      return { id: byName._id, name: byName.name, percentage: byName.percentage };
    }
    return { id: null, name, percentage: settings.tradingDealPercentage };
  }
  const pct = settings.tradingDealPercentage;
  if (pct != null && Number.isFinite(pct) && deals?.length) {
    const byPct = deals.find((d) => d.percentage === pct);
    if (byPct) return { id: byPct._id, name: byPct.name, percentage: byPct.percentage };
  }
  return { id: settings.tradingDealId, name, percentage: pct };
}

/** Value sent to API `buyingPoint` — always the selected trading deal name, never the legacy enum default. */
export function packageBuyingPointForApi(
  settings: CalculatorSettings,
  tradingDeals?: TradingDeal[]
): string {
  return resolveDealFromSettings(settings, tradingDeals).name;
}

/** Buying point label for package detail (resolves legacy "Programmatic" rows via rebate %). */
export function displayBuyingPointForPackage(
  pkg: CalculatorPackage,
  tradingDeals?: TradingDeal[],
  liveSettings?: DealDisplaySource
): string {
  if (liveSettings) {
    const fromSettings = resolveDealFromSettings(liveSettings, tradingDeals).name;
    if (fromSettings) return fromSettings;
  }

  const resolved = tradingDeals?.length
    ? resolveTradingDealFromPackage(tradingDeals, pkg)
    : { tradingDealId: null, tradingDealName: '' };
  if (resolved.tradingDealName) return resolved.tradingDealName;

  const stored = typeof pkg.buyingPoint === 'string' ? pkg.buyingPoint.trim() : '';
  if (stored && !isLegacyBuyingPointEnum(stored)) return stored;

  return '—';
}

/** Rebate % label for package detail. */
export function displayRebateForPackage(
  pkg: CalculatorPackage,
  tradingDeals?: TradingDeal[],
  liveSettings?: DealDisplaySource
): string {
  if (liveSettings) {
    const pct = resolveDealFromSettings(liveSettings, tradingDeals).percentage;
    if (pct != null && Number.isFinite(pct)) return `${pct}%`;
    const direct = parsePackageNumber(liveSettings.tradingDealPercentage);
    if (direct != null) return `${direct}%`;
  }

  let rebate = parsePackageNumber(pkg.tradingDealPercentage);
  if (rebate == null && tradingDeals?.length) {
    const resolved = resolveTradingDealFromPackage(tradingDeals, pkg);
    if (resolved.tradingDealId) {
      const deal = tradingDeals.find((d) => d._id === resolved.tradingDealId);
      if (deal) rebate = deal.percentage;
    }
  }
  if (rebate == null) {
    const stored = typeof pkg.buyingPoint === 'string' ? pkg.buyingPoint.trim() : '';
    if (stored && !isLegacyBuyingPointEnum(stored) && tradingDeals?.length) {
      const deal = tradingDeals.find((d) => d.name === stored);
      if (deal) rebate = deal.percentage;
    }
  }

  return rebate != null ? `${rebate}%` : '—';
}

/** Map a saved package document to calculator settings for prefill / sync. */
export function calculatorSettingsFromPackage(
  pkg: CalculatorPackage,
  tradingDeals?: TradingDeal[]
): Partial<CalculatorSettings> {
  const settings: Partial<CalculatorSettings> = {};

  const nested =
    pkg && typeof (pkg as unknown as Record<string, unknown>).settings === 'object'
      ? ((pkg as unknown as Record<string, unknown>).settings as Record<string, unknown>)
      : undefined;

  const totalBudget = parsePackageNumber(
    pkg.totalBudget ?? (pkg as unknown as Record<string, unknown>).total_budget ?? nested?.totalBudget
  );
  if (totalBudget != null) settings.totalBudget = totalBudget;

  const sellCPM = parsePackageNumber(
    pkg.sellCpm ?? (pkg as unknown as Record<string, unknown>).sell_cpm ?? nested?.sellCPM ?? nested?.sellCpm
  );
  if (sellCPM != null) settings.sellCPM = sellCPM;

  const hardCostsTotal = parsePackageNumber(
    pkg.hardCostsTotal ??
      (pkg as unknown as Record<string, unknown>).hard_costs_total ??
      nested?.hardCostsTotal
  );
  if (hardCostsTotal != null) settings.hardCostsTotal = hardCostsTotal;

  const tradingDealPercentage = parsePackageNumber(
    pkg.tradingDealPercentage ??
      (pkg as unknown as Record<string, unknown>).trading_deal_percentage ??
      nested?.tradingDealPercentage
  );
  if (tradingDealPercentage != null) settings.tradingDealPercentage = tradingDealPercentage;

  const displayCurrency = parseDisplayCurrency(
    pkg.currency ?? (nested?.displayCurrency as string) ?? (nested?.currency as string)
  );
  if (displayCurrency) settings.displayCurrency = displayCurrency;

  const buyingPointEnum = parseBuyingPointEnum(pkg.buyingPoint);
  if (buyingPointEnum) settings.buyingPoint = buyingPointEnum;

  if (tradingDeals?.length) {
    const resolved = resolveTradingDealFromPackage(tradingDeals, pkg);
    settings.tradingDealId = resolved.tradingDealId;
    settings.tradingDealName = resolved.tradingDealName;
  } else if (
    typeof pkg.buyingPoint === 'string' &&
    pkg.buyingPoint.trim() &&
    !isLegacyBuyingPointEnum(pkg.buyingPoint)
  ) {
    settings.tradingDealName = pkg.buyingPoint.trim();
  }

  return settings;
}

/** Trading-deal fields only — safe to apply when deals load without resetting budget / rows. */
export function tradingDealSettingsFromPackage(
  pkg: CalculatorPackage,
  tradingDeals: TradingDeal[]
): Partial<CalculatorSettings> {
  if (!tradingDeals.length) return {};

  const resolved = resolveTradingDealFromPackage(tradingDeals, pkg);
  const patch: Partial<CalculatorSettings> = {
    tradingDealId: resolved.tradingDealId,
    tradingDealName: resolved.tradingDealName
  };

  const tradingDealPercentage = parsePackageNumber(pkg.tradingDealPercentage);
  if (tradingDealPercentage != null) {
    patch.tradingDealPercentage = tradingDealPercentage;
  } else if (resolved.tradingDealId) {
    const deal = tradingDeals.find((d) => d._id === resolved.tradingDealId);
    if (deal) patch.tradingDealPercentage = deal.percentage;
  }

  return patch;
}

export function fxRatesFromPackage(pkg: CalculatorPackage): Partial<FXRates> | undefined {
  if (!pkg.fxRates || typeof pkg.fxRates !== 'object') return undefined;
  return pkg.fxRates as Partial<FXRates>;
}
